
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

export default function Products() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const productFeatures = [
    {
      icon: 'ri-flask-line',
      title: 'High Purity',
      description: 'Premium grade Light Magnesium Carbonate with exceptional purity levels'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Assured',
      description: 'Rigorous testing and quality control at every stage of production'
    },
    {
      icon: 'ri-truck-line',
      title: 'Reliable Supply',
      description: 'Consistent availability and timely delivery across India'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Technical Support',
      description: 'Expert guidance and technical assistance for optimal usage'
    }
  ];

  const specifications = [
    { parameter: 'Chemical Formula', value: 'MgCO3' },
    { parameter: 'Appearance', value: 'White Fine Powder' },
    { parameter: 'Purity', value: '≥ 98%' },
    { parameter: 'Moisture Content', value: '≤ 1.0%' },
    { parameter: 'pH Value', value: '9.0 - 10.5' },
    { parameter: 'Particle Size', value: 'As per requirement' }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative pt-24 pb-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 62, 126, 0.8), rgba(0, 62, 126, 0.6)), url('https://readdy.ai/api/search-image?query=light%20magnesium%20carbonate%20powder%20manufacturing%2C%20industrial%20chemical%20production%2C%20white%20crystalline%20powder%2C%20professional%20product%20photography%2C%20clean%20manufacturing%20environment&width=1920&height=600&seq=products-hero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-6 text-center text-white">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Products</h1>
            <p className="text-xl md:text-2xl text-blue-100">
              Premium Light Magnesium Carbonate for Industrial Applications
            </p>
          </div>
        </div>
      </section>

      {/* Product Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-8">Light Magnesium Carbonate (MgCO3)</h2>
            <p className="text-lg text-gray-600 mb-6">
              We manufacture Light Magnesium Carbonate at our facility located at Survey No. 63/1 P-2, Near Ahmedabad–Bhavnagar Highway, Sanes – 364313, Bhavnagar, Gujarat.
            </p>
            <p className="text-lg text-gray-600">
              We ensure consistency, purity, and safety throughout the process. Our infrastructure and distribution network serve industries across India with reliable quality and timely delivery.
            </p>
          </div>

          {/* Main Product Card */}
          <div className="max-w-6xl mx-auto">
            <Card className="overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=light%20magnesium%20carbonate%20white%20powder%20sample%2C%20high%20purity%20chemical%20powder%2C%20crystalline%20texture%2C%20professional%20product%20photography%2C%20clean%20white%20background%2C%20industrial%20grade%20material&width=600&height=400&seq=main-product&orientation=landscape"
                    alt="Light Magnesium Carbonate"
                    className="w-full h-96 object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Premium Grade
                  </div>
                </div>
                <div className="p-8 lg:p-12">
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">Light Magnesium Carbonate</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    High-purity MgCO3 for multi-industry applications. Our Light Magnesium Carbonate is manufactured using advanced processes to ensure superior quality and consistency.
                  </p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-3"></i>
                      <span className="text-gray-700">Industrial Grade Quality</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-3"></i>
                      <span className="text-gray-700">Consistent Particle Size</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-3"></i>
                      <span className="text-gray-700">Low Moisture Content</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-3"></i>
                      <span className="text-gray-700">Excellent Flowability</span>
                    </div>
                  </div>
                  <Button variant="primary" size="lg">
                    Request Quote
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Product Features */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Product Features</h2>
            <p className="text-xl text-gray-600">Why our Light Magnesium Carbonate stands out</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {productFeatures.map((feature, index) => (
              <Card key={index} className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center text-white text-2xl">
                  <i className={feature.icon}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">Technical Specifications</h2>
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
                <h3 className="text-2xl font-bold">Light Magnesium Carbonate (MgCO3)</h3>
                <p className="text-blue-100">Detailed product specifications</p>
              </div>
              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {specifications.map((spec, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">{spec.parameter}:</span>
                      <span className="text-gray-600">{spec.value}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                  <h4 className="text-lg font-bold text-gray-800 mb-2">Custom Specifications Available</h4>
                  <p className="text-gray-600">
                    We can customize our Light Magnesium Carbonate to meet specific requirements for particle size, purity levels, and packaging options based on your application needs.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">Manufacturing Excellence</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Our Manufacturing Process</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Raw Material Selection</h4>
                      <p className="text-gray-600">Careful selection of high-quality raw materials</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Controlled Processing</h4>
                      <p className="text-gray-600">Advanced manufacturing techniques for optimal quality</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Quality Testing</h4>
                      <p className="text-gray-600">Rigorous testing at multiple stages</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Packaging & Delivery</h4>
                      <p className="text-gray-600">Secure packaging and timely delivery</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=chemical%20manufacturing%20process%20flow%2C%20industrial%20equipment%2C%20quality%20control%20systems%2C%20modern%20production%20line%2C%20professional%20manufacturing%20environment%2C%20Gujarat%20facility&width=600&height=400&seq=manufacturing-process&orientation=landscape"
                  alt="Manufacturing Process"
                  className="rounded-xl shadow-2xl object-cover w-full h-96"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
